import { useCallback, useEffect, useRef, useState } from "react"
import VanSession, { VAN_STATUS } from "../van/VanSession"
import { VAN_RELAY_BASE, fetchVanMemory } from "../network/van"
import { pollAndSaveSummary, resumePendingSummaries } from "../van/summaryPoller"
import { useSession } from "../auth/session"

// Owns one VAN session for as long as the sheet is open.
export default function useVanSession({ itemId, isOpen }) {
  const { restaurant, user } = useSession()
  const [status, setStatus] = useState(VAN_STATUS.IDLE)
  const [isVanSpeaking, setIsVanSpeaking] = useState(false)
  const [error, setError] = useState(null)
  const sessionRef = useRef(null)

  useEffect(() => {
    if (!isOpen || !itemId || !restaurant?.id) return undefined

    let disposed = false
    let oldMemory = ""
    resumePendingSummaries(user?.id)
    const session = new VanSession({
      relayBase: VAN_RELAY_BASE,
      onStatus: (next) => !disposed && setStatus(next),
      onError: (message) => !disposed && setError(message),
      onVanSpeaking: (speaking) => !disposed && setIsVanSpeaking(speaking),
      // Outlives the sheet: the summary is polled and saved after it closes.
      onSessionEnded: (sessionId) =>
        pollAndSaveSummary({
          sessionId,
          restaurantId: restaurant.id,
          itemId,
          fallbackMemory: oldMemory,
          userId: user?.id
        })
    })
    sessionRef.current = session

    fetchVanMemory(itemId)
      .catch(() => "")
      .then((memory) => {
        oldMemory = memory
        return session.start({ restaurantId: restaurant.id, itemId, oldMemory: memory })
      })
      .catch((startError) => {
        if (disposed) return
        setStatus(VAN_STATUS.ERROR)
        setError(readableError(startError))
      })

    return () => {
      disposed = true
      session.close()
      sessionRef.current = null
    }
  }, [isOpen, itemId, restaurant?.id, user?.id])

  const startTurn = useCallback(() => sessionRef.current?.startTurn(), [])
  const endTurn = useCallback(() => sessionRef.current?.endTurn(), [])

  return { status, error, isVanSpeaking, startTurn, endTurn }
}

function readableError(error) {
  if (error?.name === "NotAllowedError") return "Microphone access was denied."
  if (error?.name === "NotFoundError") return "No microphone was found."
  return error?.message ?? "Could not reach VAN."
}
